'use client';

import { SvgChatbubbleQuestionFill } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { CustomLink } from '@/app/_components/CustomLink';
import { useGiftCart } from '@/app/donation/_hooks/useGiftCart';
import Logo from '@/assets/svgs/logo.svg';
import { HIDE_DONATION_HEADER_PATHNAMES } from '@/components/template/GlobalLayout/constants';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useLogout } from '@/hooks/useLogout';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import { useCategory } from './_hooks/useCategory';
import { Agencies } from './Agencies';
import { CategoryDropdown } from './CategoryDropdown';
import {
  DONATION_HEADER_BOTTOM_HEIGHT,
  DONATION_HEADER_TOP_HEIGHT,
} from './constants';
import { Search } from './Search';

export const DonationDesktopHeader = () => {
  const isDonationDesktopHeaderShow = useGlobalStore(
    useShallow((state) => state.isDonationDesktopHeaderShow),
  );
  const { logout } = useLogout();
  const isLogin = useAuthStore((state) => state.isLogin);
  const pathname = usePathname();
  const { totalCount } = useGiftCart();
  const hasCartItems = totalCount > 0;

  const { categoryData } = useCategory();

  const handleLogout = () => {
    logout({
      redirect: DONATION_ROUTES.HOME.pathname,
      toastOptions: {
        message: '로그아웃 되었습니다.',
        type: 'success',
      },
    });
  };

  const isHiddenHeaderPage = HIDE_DONATION_HEADER_PATHNAMES.includes(
    pathname ?? '',
  );

  return (
    <header
      className={cn(
        'fixed top-0 z-20 min-h-138 w-vw bg-Gray-white',
        isDonationDesktopHeaderShow && !isHiddenHeaderPage
          ? 'mobile:hidden tablet:hidden'
          : 'hidden',
      )}
      data-donation-header={
        isDonationDesktopHeaderShow && !isHiddenHeaderPage ? 'show' : 'hide'
      }
    >
      <nav className="mx-auto max-w-screen-xl px-40">
        <div
          className="flex justify-between"
          style={{
            height: DONATION_HEADER_TOP_HEIGHT,
          }}
        >
          <div className="flex items-center gap-16">
            <CustomLink
              href={ROUTES.HOME.pathname}
              className="flex h-full shrink-0 items-center"
              scrollHistory={false}
            >
              <Logo className="h-35 w-106" />
            </CustomLink>
            <CustomLink
              href={DONATION_ROUTES.HOME.pathname}
              className="flex h-full shrink-0 items-center whitespace-nowrap font-700 text-Gray-500 transition-colors duration-500 typo-h6 hover:text-Gray-600"
              scrollHistory={false}
            >
              고향사랑기부
            </CustomLink>
          </div>
          <div className="flex items-center">
            <Search />
          </div>
          <Agencies />
        </div>
        <div
          className="flex items-center justify-between gap-8"
          style={{
            height: DONATION_HEADER_BOTTOM_HEIGHT,
          }}
        >
          <ul className="flex">
            <li>
              <CategoryDropdown categories={categoryData} className="-ml-24" />
            </li>
            <li>
              <SubLink
                href={DONATION_ROUTES.GIFTS.pathname}
                isSelected={DONATION_ROUTES.GIFTS.pathname === pathname}
              >
                답례품
              </SubLink>
            </li>
            <li>
              <SubLink
                href={DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname}
                isSelected={
                  DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname === pathname
                }
              >
                지정기부사업
              </SubLink>
            </li>
            <li>
              <SubLink
                href={DONATION_ROUTES.MY_ETC_EVENT.pathname}
                isSelected={DONATION_ROUTES.MY_ETC_EVENT.pathname === pathname}
              >
                이벤트
              </SubLink>
            </li>
            <li>
              <SubLink
                href={DONATION_ROUTES.GUIDE.pathname}
                isSelected={DONATION_ROUTES.GUIDE.pathname === pathname}
              >
                고향사랑기부 안내
                <SvgChatbubbleQuestionFill className="ml-4 size-16 text-B-500" />
              </SubLink>
            </li>
          </ul>
          <div className="-mr-8 flex items-center gap-4">
            {!isLogin ? (
              <>
                <CustomLink
                  href={ROUTES.LOGIN.withSearchParams({
                    searchParams: {
                      redirect: pathname || undefined,
                    },
                  })}
                  className="p-8 font-400 text-Gray-black typo-body4"
                >
                  로그인
                </CustomLink>
                <div className="h-12 border-l-1 border-Gray-300" />
                <CustomLink
                  href={ROUTES.LOGIN.withSearchParams({
                    searchParams: {
                      redirect: pathname || undefined,
                    },
                  })}
                  className="p-8 font-400 text-Gray-black typo-body4"
                >
                  회원가입
                </CustomLink>
              </>
            ) : (
              <button
                type="button"
                onClick={handleLogout}
                className="p-8 font-400 text-Gray-black typo-body4"
              >
                로그아웃
              </button>
            )}
            <div className="h-12 border-l-1 border-Gray-300" />
            <CustomLink
              href={DONATION_ROUTES.MY.pathname}
              className="p-8 font-400 text-Gray-black typo-body4"
            >
              마이페이지
            </CustomLink>
            <div className="h-12 border-l-1 border-Gray-300" />
            <CustomLink
              href={DONATION_ROUTES.CART.pathname}
              className="relative flex flex-row items-center  gap-4 p-8 font-400 text-Gray-black typo-body4"
            >
              장바구니
              {hasCartItems && (
                <span className="flex size-16 items-center justify-center rounded-pill bg-O-400 font-700 text-Gray-white typo-caption2">
                  {totalCount}
                </span>
              )}
            </CustomLink>
          </div>
        </div>
      </nav>
    </header>
  );
};

const SubLink = ({
  href,
  isSelected,
  className,
  children,
}: {
  href: string;
  isSelected?: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <CustomLink
      href={href}
      className={cn(
        'flex h-full min-h-56 items-center px-24 font-600 text-Gray-500 typo-body2',
        {
          'text-Gray-800': isSelected,
        },
        className,
      )}
      scrollHistory={false}
    >
      {children}
    </CustomLink>
  );
};
